import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, Mail, Phone, Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "../hooks/usePageMeta";
import { SITE } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ff_admin_token";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3.5 text-sm text-navy placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-azure focus:bg-white focus:ring-4 focus:ring-[rgba(36,85,138,0.12)]";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  usePageMeta("Area riservata");
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/contact-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setRequests(res.data.requests || []))
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        } else {
          toast.error("Errore nel caricamento delle richieste.");
          setRequests([]);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setRequests(null);
  };

  return (
    <main className="pt-40 pb-20 lg:pt-48 lg:pb-28 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10">
        {!token ? (
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <img src={SITE.logo} alt={SITE.name} className="h-11 w-auto mx-auto mb-6" />
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-navy">
                Area riservata
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                Accedi per consultare le richieste ricevute dal sito.
              </p>
            </div>
            <form
              data-testid="admin-login-form"
              onSubmit={onLogin}
              className="bg-white rounded-[1.6rem] border border-slate-200 p-8 shadow-[0_30px_70px_-40px_rgba(8,42,89,0.3)]"
            >
              <label htmlFor="admin-email" className="block text-xs font-bold uppercase tracking-[0.14em] text-navy/70 mb-2">
                Email
              </label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="La tua email"
                className={inputCls}
              />
              <label htmlFor="admin-password" className="block text-xs font-bold uppercase tracking-[0.14em] text-navy/70 mt-5 mb-2">
                Password
              </label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="La tua password"
                className={inputCls}
              />
              {error && (
                <p data-testid="admin-login-error" className="mt-4 text-sm font-semibold text-red-600">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="admin-login-submit"
                disabled={loading}
                className="mt-7 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-azure px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-[#1C446E] disabled:opacity-60"
              >
                {loading ? <Loader2 size={17} className="animate-spin" /> : <Lock size={16} />}
                Accedi
              </button>
            </form>
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-navy">
                  Richieste ricevute
                </h1>
                <p className="mt-1.5 text-sm text-slate-500">
                  {requests ? `${requests.length} richieste dal modulo contatti` : "Caricamento…"}
                </p>
              </div>
              <button
                data-testid="admin-logout-button"
                onClick={onLogout}
                className="inline-flex items-center gap-2 rounded-full border-2 border-navy/15 px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:border-navy"
              >
                <LogOut size={15} />
                Esci
              </button>
            </div>

            {loading && !requests ? (
              <div className="flex justify-center py-20">
                <Loader2 size={28} className="animate-spin text-azure" />
              </div>
            ) : requests && requests.length === 0 ? (
              <div data-testid="admin-empty-state" className="rounded-[1.6rem] border-2 border-dashed border-slate-300 bg-white/60 px-8 py-16 text-center">
                <Inbox size={30} className="mx-auto text-slate-300 mb-4" />
                <p className="text-sm text-slate-500">Nessuna richiesta ricevuta finora.</p>
              </div>
            ) : (
              <div data-testid="admin-requests-list" className="space-y-5">
                {requests?.map((r, i) => (
                  <div
                    key={i}
                    data-testid={`admin-request-card-${i}`}
                    className="bg-white rounded-[1.4rem] border border-slate-200 p-6 sm:p-7"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                      <div>
                        <p className="text-base font-bold text-navy">{r.nome}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{formatDate(r.created_at)}</p>
                      </div>
                      <span className="rounded-full bg-azure-light text-azure px-3.5 py-1.5 text-xs font-bold">
                        {r.percorso}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-8 gap-y-2 mb-4 text-sm">
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-2 text-azure font-semibold">
                        <Mail size={14} /> {r.email}
                      </a>
                      <a href={`tel:${r.telefono}`} className="inline-flex items-center gap-2 text-navy/80 font-semibold">
                        <Phone size={14} /> {r.telefono}
                      </a>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-xl px-4 py-3">
                      {r.messaggio}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
